import 'lazysizes'
import { forwardRef, type MutableRefObject, useEffect } from 'react'
import { Image } from '../ResponsiveImage/ResponsiveImage.styles'
import type { ResponsivePictureProps } from './ResponsivePicture.types'

const ResponsivePicture = forwardRef<
  HTMLPictureElement,
  ResponsivePictureProps
>((props, ref) => {
  const {
    mediaSources,
    className = '',
    lqip = false,
    blurRadius = 5,
    transitionDuration = 400,
    sizes,
    imgSrc,
    showDefault,
    imgDataSrc,
    lazyload = false,
  } = props

  useEffect(() => {
    if (!(ref as MutableRefObject<HTMLImageElement> | null)?.current) {
      return
    }
    ;(ref as MutableRefObject<HTMLImageElement> | null)?.current
      .querySelector('img')
      ?.classList.add('lazyload')
  }, [mediaSources, ref])

  return (
    <picture ref={ref}>
      {mediaSources?.map(
        ({
          id,
          sources: mediaSource,
          media,
          sizes: mediaSizes,
          imgSrc: mediaImgSrc,
        }) => {
          const srcSet = mediaSource
            .map((source) => `${source.src} ${source.width}`)
            .join(', ')

          return (
            <source
              key={id}
              media={media}
              {...(lazyload
                ? { 'data-sizes': mediaSizes ?? 'auto' }
                : { sizes: mediaSizes ?? 'auto' })}
              {...(lazyload
                ? {
                    'data-srcset': srcSet,
                    srcSet: mediaImgSrc,
                  }
                : { srcSet })}
            />
          )
        },
      )}

      <Image
        lqip={lqip}
        className={`${className} ${lazyload ? 'lazyload' : ''}`}
        blurRadius={blurRadius}
        transitionDuration={transitionDuration}
        src={
          imgSrc ??
          (showDefault
            ? 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdjOHPmzH8ACDADZKt3GNsAAAAASUVORK5CYII='
            : '')
        }
        {...(imgDataSrc && { 'data-src': imgDataSrc })}
        {...(lazyload && { 'data-sizes': sizes ?? 'auto' })}
      />
    </picture>
  )
})

ResponsivePicture.displayName = 'ResponsivePicture'

export { ResponsivePicture }
